import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import CameraCapture from './pages/CameraCapture';
import Selector from './pages/ModeSelector';
import Settings from './pages/Settings/SettingsHome';
import CardsMeaning from './pages/CardsMeaning/CardsMeaning';
import Home from './pages/Home';
import CardSelectionDebug from './components/reading/CardSelectionDebug';

import ReadingCards from './pages/ReadingCards';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';
import { RecipeProvider } from './context/RecipeContext';
import { LanguageProvider } from './context/LanguageContext';
import { AudioPlayerProvider } from './context/AudioPlayerContext';
import { RNBridgeProvider } from './context/RNBridgeContext';
import { LogProvider } from './context/LogContext';
import Layout from './layout/Layout';

import { GoogleOAuthProvider } from '@react-oauth/google';
import NotificationsPage from './pages/Notifications';

import SpreadCards from './pages/Reading/SelectReading';
import Reading from './pages/Reading/Reading'
import ReadingList from './pages/Reading/ReadingList';

import PageLayout from './layout/PageLayout';
import Login from './pages/login/Login';
import Register from './pages/login/Register';
import ForgotPassword from './pages/login/ForgotPassword'
import JournalPage from './pages/Journal/Journal';
import JournalEntryPage from './pages/Journal/JournalEntryPage';
import DisplayReading from './pages/Reading/DisplayReading';
import PersonalProfile from './pages/Settings/PersonalProfile';
import PersonalInformation from './pages/Settings/PersonalInformation';
import Onboarding from './pages/onboarding/Onboarding';
import Billing from './pages/Settings/Billing';
import Feedback from './pages/Feedback';
import { CardsProvider } from './context/CardsContext';
import useCards from '@/hooks/useCards';
import { useRecipe } from '@/context/RecipeContext';
import SplashScreen from './pages/SplashScreen';
import Chat from './pages/chat/Chat';
import { DailyReadingProvider } from './context/DailyReadingContext';
import WelcomeSpread from './pages/WelcomeSpread';
import WelcomeSpreadLayout from './layout/WelcomeSpreadLayout';
import { WelcomeSpreadProvider } from './context/WelcomeSpreadContext';
import { FeatureFlagProvider } from './context/FeatureFlagContext';


const NavigationCheck = () => {
  const navigate = useNavigate();
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);


  useEffect(() => {
    if (!isMobile && location.pathname !== '/splash-screen') {
      navigate('/splash-screen');
    }
  }, [navigate, isMobile]);

  return null;
}

const UserDataLoader = () => {
  const { refreshUserData } = useAuth();
  const hasRefreshed = React.useRef(false);
  const { getReadings } = useCards();
  const { getJournals } = useRecipe();

  useEffect(() => {
    if (refreshUserData && !hasRefreshed.current) {
      hasRefreshed.current = true;

      // First refresh user data, then load readings and journals
      const loadData = async () => {
        try {

          await refreshUserData();
          await getReadings();
          await getJournals();
        } catch (error) {
          console.error('Error loading user data:', error);
        }
      };

      loadData();
    }
  }, [refreshUserData, getReadings, getJournals]);

  return null;
}

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Immediate scroll
    window.scrollTo(0, 0);

    // Delayed scroll to handle dynamic content
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [pathname]);

  return null;
};

const App: React.FC = () => {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

  return (
    <FeatureFlagProvider>
      <CardsProvider>
        <GoogleOAuthProvider clientId={clientId}>
          <ChatProvider>
            <RecipeProvider>
              <AuthProvider>
                <LanguageProvider>
                  <AudioPlayerProvider>
                    <RNBridgeProvider>
                      <LogProvider>
                        <DailyReadingProvider>
                          <Router>
                        <ScrollToTop />
                        <NavigationCheck />
                        <UserDataLoader />
                        <Routes>
                          <Route path="/splash-screen" Component={SplashScreen} />
                          <Route
                            path="/welcome-spread"
                            element={
                              <WelcomeSpreadProvider>
                                <WelcomeSpreadLayout>
                                  <WelcomeSpread />
                                </WelcomeSpreadLayout>
                              </WelcomeSpreadProvider>
                            }
                          />
                          <Route path="/login" Component={Login} />
                          <Route path="/register" Component={Register} />
                          <Route path="/forgot-password" Component={ForgotPassword} />

                          {/* needs to be simplified for being less verbose*/}
                          <Route
                            path="/camera"
                            element={
                              <ProtectedRoute>
                                <CameraCapture />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/onboarding"
                            element={
                              <ProtectedRoute>
                                <Onboarding />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/chat/:id"
                            element={
                              <ProtectedRoute>
                                <Chat />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/journal"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.journal'}>
                                  <JournalPage />
                                </PageLayout>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/journal/:id"
                            element={
                              <ProtectedRoute>
                                <JournalEntryPage />
                              </ProtectedRoute>
                            }
                          />


                          <Route
                            path="/"
                            element={<Navigate to="/home" replace />}
                          />
                          <Route
                            path="/home"
                            element={
                              <ProtectedRoute>
                                <Layout>
                                  <Home />
                                </Layout>
                              </ProtectedRoute>
                            }
                          />                             <Route
                            path="/settings/feedback"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.shareFeedback'}>
                                  <Feedback />
                                </PageLayout>
                              </ProtectedRoute>
                            }
                          />


                          <Route
                            path="/reading-cards"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.readingCards'} >
                                  <ReadingCards />
                                </PageLayout>
                              </ProtectedRoute>
                            }
                          />

                          <Route
                            path="/reading/:id"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.reading'}>
                                  <Reading />
                                </PageLayout>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/reading"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.reading'}>
                                  <Reading />
                                </PageLayout>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/display-reading/:id"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.displayReading'}>
                                  <DisplayReading />
                                </PageLayout>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/mode-selector"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.modeSelector'}>
                                  <Selector />
                                </PageLayout>
                              </ProtectedRoute>
                            }
                          />

                          <Route
                            path="/spread-cards"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.selectReading'}>
                                  <SpreadCards />
                                </PageLayout>

                              </ProtectedRoute>
                            }
                          />


                          <Route
                            path="/settings"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.settings'}>
                                  <Settings />
                                </PageLayout>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/settings/payments"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.payments'}>
                                  <Billing onSuccess={() => { }} padding={2} />
                                </PageLayout>
                              </ProtectedRoute>
                            }
                          />

                          <Route
                            path="/settings/personal-information"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.personalInformation'}>
                                  <PersonalInformation />
                                </PageLayout>
                              </ProtectedRoute>
                            } />

                          <Route
                            path="/settings/personal-profile"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.personalProfile'}>
                                  <PersonalProfile />
                                </PageLayout>
                              </ProtectedRoute>
                            } />

                          <Route
                            path="/reading-list"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.readingList'}>
                                  <ReadingList />
                                </PageLayout>
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/notifications"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.notifications'}>
                                  <NotificationsPage />
                                </PageLayout>
                              </ProtectedRoute>
                            }
                          />

                          <Route
                            path="/cards-meaning"
                            element={
                              <ProtectedRoute>
                                <PageLayout titleKey={'pages.cardMeanings'}>
                                  <CardsMeaning />
                                </PageLayout>
                              </ProtectedRoute>
                            }
                          />

                          <Route
                            path="/debug-shuffle"
                            element={
                              <CardSelectionDebug />
                            }
                          />

                        </Routes>
                          </Router>
                        </DailyReadingProvider>
                      </LogProvider>
                    </RNBridgeProvider>
                  </AudioPlayerProvider>
                </LanguageProvider>
              </AuthProvider>
            </RecipeProvider>
          </ChatProvider>
        </GoogleOAuthProvider>
      </CardsProvider>
    </FeatureFlagProvider>
  );
};

export default App;
